import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, catchError, map, of, take } from 'rxjs';

import { FeatureFlagService } from '../global-store';
import { AdvisorDetailsModel, RestrictedFeature } from '../model';
import { AccountAdvisorService } from '../service';

export const FREE_PLAN_USER_FEATURE: string = 'Beraterzugang Free';

/** Features unlocked by a feature flag (add-ons, Plus Investment depots) — independent of the Free package. */
export const RESTRICTED_FEATURE_ADDON_FLAGS: Partial<Record<RestrictedFeature, string>> = {
  chats: 'CHAT',
  insign: 'INSIGN',
  'media-studio': 'MEDIA_STUDIO',
  depots: 'DEPOTS',
};

export const ADMINISTRATOR_USER_FEATURE: string = 'Administrator';

/** Administrators manage the company's subscription (Abo page); other advisors have to ask them. */
export const isAdministratorAdvisor = (advisorDetails: AdvisorDetailsModel | null | undefined): boolean =>
  !!advisorDetails?.userFeatures?.includes(ADMINISTRATOR_USER_FEATURE);

export const isFreePlanAdvisor = (advisorDetails: AdvisorDetailsModel | null | undefined): boolean =>
  !!advisorDetails?.userFeatures?.includes(FREE_PLAN_USER_FEATURE);

/**
 * Without access the advisor gets the restricted (upsell) page of the feature instead of the real page:
 * - add-on features: access only with the add-on feature flag
 * - package features: no access on the Free package
 */
export const restrictedFeatureGuard =
  (feature: RestrictedFeature): CanActivateFn =>
  (): Observable<boolean | UrlTree> => {
    const router = inject(Router);
    const restrictedUrl: UrlTree = router.createUrlTree(['/restricted-product', feature]);
    const addonFlag: string | undefined = RESTRICTED_FEATURE_ADDON_FLAGS[feature];

    if (addonFlag) {
      return inject(FeatureFlagService)
        .isFeatureEnabled(addonFlag)
        .pipe(
          take(1),
          map((enabled: boolean): boolean | UrlTree => enabled || restrictedUrl),
        );
    }

    return inject(AccountAdvisorService).advisorDetails.observable.pipe(
      take(1),
      map((advisorDetails: AdvisorDetailsModel): boolean | UrlTree =>
        isFreePlanAdvisor(advisorDetails) ? restrictedUrl : true,
      ),
      catchError(() => of(true)),
    );
  };
